import React, { useState } from 'react';
import { Clock, CheckCircle, AlertTriangle, Calendar, TrendingUp, XCircle, Search, PhoneCall, ChevronDown, ChevronUp, Hash } from 'lucide-react';

const OUTCOMES = {
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  escalated: { label: 'Escalated', color: 'bg-orange-100 text-orange-800', icon: TrendingUp },
  callback_scheduled: { label: 'Callback', color: 'bg-blue-100 text-blue-800', icon: Calendar },
  no_answer: { label: 'No Answer', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const CALL_REASONS = {
  claims_issue: { label: 'Claims', color: 'bg-red-100 text-red-700' },
  billing: { label: 'Billing', color: 'bg-orange-100 text-orange-700' },
  prior_auth: { label: 'Prior Auth', color: 'bg-purple-100 text-purple-700' },
  enrollment_status: { label: 'Enrollment', color: 'bg-blue-100 text-blue-700' },
  id_card: { label: 'ID Card', color: 'bg-cyan-100 text-cyan-700' },
  benefits_question: { label: 'Benefits', color: 'bg-teal-100 text-teal-700' },
  provider_search: { label: 'Provider', color: 'bg-green-100 text-green-700' },
  rx_issue: { label: 'Rx Issue', color: 'bg-pink-100 text-pink-700' },
  appeal: { label: 'Appeal', color: 'bg-red-100 text-red-700' },
  disenrollment: { label: 'Disenroll', color: 'bg-slate-100 text-slate-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700' },
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ServiceHistoryList({ events = [], onAddOutcome }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const filtered = events.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.carrier_name || '').toLowerCase().includes(q) ||
           (e.call_reason || '').toLowerCase().includes(q) ||
           (e.reference_number || '').toLowerCase().includes(q) ||
           (e.outcome_notes || '').toLowerCase().includes(q);
  });

  const displayed = showAll ? filtered : filtered.slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
      >
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Clock size={15} className="text-slate-500" />
          Service History
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
            {events.length}
          </span>
        </h3>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {expanded && (
        <div>
          {events.length > 3 && (
            <div className="px-4 pt-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by carrier, reason, ref#..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none"
                />
              </div>
            </div>
          )}

          <div className="p-3 space-y-2">
            {displayed.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">
                {search ? 'No matching events' : 'No service history yet'}
              </p>
            )}
            {displayed.map(event => {
              const reason = CALL_REASONS[event.call_reason] || CALL_REASONS.other;
              const outcome = event.outcome ? OUTCOMES[event.outcome] : null;
              const OutcomeIcon = outcome?.icon;

              return (
                <div key={event.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors bg-slate-50/50">
                  <div className="flex-shrink-0 mt-0.5">
                    <PhoneCall size={14} className="text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-700">{event.carrier_name || 'Unknown'}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${reason.color}`}>
                        {reason.label}
                      </span>
                      {outcome && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${outcome.color}`}>
                          {OutcomeIcon && <OutcomeIcon size={10} />}
                          {outcome.label}
                        </span>
                      )}
                      {!event.outcome && onAddOutcome && (
                        <button
                          onClick={() => onAddOutcome(event)}
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                        >
                          + Outcome
                        </button>
                      )}
                    </div>
                    {event.call_reason_detail && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{event.call_reason_detail}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {timeAgo(event.created_at)}
                      </span>
                      {event.reference_number && (
                        <span className="flex items-center gap-1 font-mono">
                          <Hash size={10} /> {event.reference_number}
                        </span>
                      )}
                      {event.phone_dialed && (
                        <span className="font-mono">{event.phone_dialed}</span>
                      )}
                    </div>
                    {event.outcome_notes && (
                      <p className="text-[11px] text-slate-500 mt-1 italic bg-white rounded px-2 py-1 border border-slate-100">
                        {event.outcome_notes}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length > 10 && !showAll && (
            <div className="px-4 pb-3">
              <button
                onClick={() => setShowAll(true)}
                className="w-full text-xs text-indigo-600 hover:text-indigo-800 font-medium py-2 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                Show all {filtered.length} events
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
